/** What each item category and altitude level means — the guides on the item page. */
import { AlertCircle, FlaskConical, Landmark, Mountain, ShieldAlert, TrendingUp, Wrench } from 'lucide-react';
import type { InfoSection } from '../InfoModal';

export const CATEGORY_GUIDE_SECTIONS: InfoSection[] = [
  {
    id: 'problem',
    label: 'Problem',
    tagline: 'Something is wrong that needs fixing.',
    description: 'A Problem describes a pain point, gap, defect, or undesirable condition. It describes the item in terms of what is broken or absent. Use this when the root cause or symptom is clear and the goal is to eliminate it.',
    examples: ['Customer churn is increasing in the mid-market segment', 'Login failure rate spikes during peak hours', 'Engineering teams spend 30% of their time on unplanned work'],
    note: 'Problems range from root causes (altitude 5) to specific bugs (altitude 1).',
    color: 'bg-[var(--mustard-dim)] border-[var(--mustard)]', iconColor: 'text-[var(--mustard)]', Icon: AlertCircle,
  },
  {
    id: 'opportunity',
    label: 'Opportunity',
    tagline: 'Something good we could pursue.',
    description: 'An Opportunity describes the item as a possibility — a chance to grow, improve, or create value. It\'s forward-looking and additive. Use this when the item represents potential upside rather than a problem to fix.',
    examples: ['Expanding into the SMB segment could double our TAM', 'Adding SSO would unlock enterprise deals', 'A referral program could reduce CAC by 20%'],
    note: 'Opportunities range from strategies (altitude 5) to quick wins (altitude 1).',
    color: 'bg-[var(--green-dim)] border-[var(--green)]', iconColor: 'text-[var(--green)]', Icon: TrendingUp,
  },
  {
    id: 'risk',
    label: 'Risk',
    tagline: 'Something that could go wrong.',
    description: 'A Risk is a threat or exposure — something that hasn\'t happened yet but could. Use this when the item is about mitigating, monitoring, or hedging against a potential negative outcome.',
    examples: ['Third-party payment provider could deprecate our API', 'Key personnel dependencies on two engineers who know the legacy system', 'Regulatory change could require re-architecture by Q4'],
    note: 'Risks range from existential threats (altitude 5) to minor flags (altitude 1).',
    color: 'bg-[var(--coral-dim)] border-[var(--coral)]', iconColor: 'text-[var(--coral)]', Icon: ShieldAlert,
  },
  {
    id: 'solution',
    label: 'Solution',
    tagline: 'A specific thing to build or implement.',
    description: 'A Solution describes work to be done — a feature, fix, or implementation. It\'s the "how" in response to a Problem or Opportunity. Most engineering backlog items are Solutions.',
    examples: ['Build a self-serve password reset flow', 'Migrate authentication to OAuth 2.0', 'Implement rate limiting on the public API'],
    note: 'Solutions range from platforms (altitude 5) to tasks (altitude 1).',
    color: 'bg-[var(--slate-dim)] border-[var(--slate)]', iconColor: 'text-[var(--text-ter)]', Icon: Wrench,
  },
  {
    id: 'commitment',
    label: 'Commitment',
    tagline: 'Something we\'ve promised to do.',
    description: 'A Commitment is work that is obligated — either by a contract, a public announcement, a legal requirement, or a firm internal pledge. Commitments have lower prioritization flexibility because they\'re already agreed to.',
    examples: ['SOC 2 Type II compliance by end of year (contractual)', 'Public roadmap item announced at conference', 'Migration promised to enterprise customer as part of renewal'],
    note: 'Commitments range from mandates (altitude 5) to specific contractual actions (altitude 1).',
    color: 'bg-[var(--slate-dim)] border-[var(--slate)]', iconColor: 'text-[var(--text-ter)]', Icon: Landmark,
  },
  {
    id: 'investment',
    label: 'Investment',
    tagline: 'Work that builds future capability.',
    description: 'An Investment is strategic capacity-building — work whose primary value is enabling future work rather than delivering immediate user value. Technical infrastructure, R&D, and capability bets are Investments.',
    examples: ['Migrate to a microservices architecture', 'Build internal ML platform for future product features', 'Hire and train a dedicated security team'],
    note: 'Investments range from transformations (altitude 5) to small experiments (altitude 1).',
    color: 'bg-[var(--slate-dim)] border-[var(--slate)]', iconColor: 'text-[var(--text-ter)]', Icon: FlaskConical,
  },
];

export const ALTITUDE_GUIDE_SECTIONS: InfoSection[] = [
  {
    id: '5',
    label: 'Level 5 — Strategic',
    meta: 'Long time horizon · Large scope · Org-wide impact',
    tagline: 'This reshapes how the organization operates.',
    description: 'Level 5 items are enterprise-scale. They span years, require cross-functional coordination, and affect the entire organization or a major business unit. Examples by category: Platform (Solution), Transformation (Investment), Strategy (Opportunity), Crisis (Risk), Root Cause (Problem), Mandate (Commitment).',
    color: 'bg-[var(--bg)] border-[var(--rule)]',
    iconColor: 'text-white',
    Icon: Mountain,
  },
  {
    id: '4',
    label: 'Level 4 — Programmatic',
    meta: 'Multi-quarter · Significant scope · Multi-team impact',
    description: 'Level 4 items span multiple quarters and require coordination across teams. They\'re too big for a single team to own end-to-end. Examples: Initiative (Solution), Portfolio (Investment), Program (Opportunity), Threat (Risk), Pattern (Problem), Pledge (Commitment).',
    color: 'bg-[var(--bg-raised)] border-[var(--rule)]',
    iconColor: 'text-white',
    Icon: Mountain,
  },
  {
    id: '3',
    label: 'Level 3 — Initiative',
    meta: 'Quarter-scale · Meaningful scope · Team-level impact',
    description: 'Level 3 items are owned by a single team and deliverable within a quarter. They represent significant chunks of work with clear outcomes. Examples: Epic (Solution), Project (Investment), Initiative (Opportunity), Exposure (Risk), Gap (Problem), Contract (Commitment).',
    color: 'bg-[var(--hover-tint)] border-[var(--rule)]',
    iconColor: 'text-[var(--text-sec)]',
    Icon: Mountain,
  },
  {
    id: '2',
    label: 'Level 2 — Tactical',
    meta: 'Sprint-scale · Contained scope · Squad or pod impact',
    description: 'Level 2 items fit within a sprint and are owned by a small sub-team or individual squad. Examples: Story (Solution), Pilot (Investment), Prospect (Opportunity), Concern (Risk), Defect (Problem), Deliverable (Commitment).',
    color: 'bg-[var(--surface)] border-[var(--rule)]',
    iconColor: 'text-[var(--text-sec)]',
    Icon: Mountain,
  },
  {
    id: '1',
    label: 'Level 1 — Operational',
    meta: 'Days or hours · Narrow scope · Individual impact',
    description: 'Level 1 items are small, discrete units of work that a single person can complete in a day or two. Examples: Task (Solution), Experiment (Investment), Quick Win (Opportunity), Flag (Risk), Bug (Problem), Action (Commitment).',
    color: 'bg-[var(--bg-raised)] border-[var(--rule)]',
    iconColor: 'text-[var(--text-ter)]',
    Icon: Mountain,
  },
];
